import useOrganizations from '../models/useOrganizations'
import { getUserId } from '../lib/workos'
import { allows, mayManageNode, type OrgAction, type OrgRole } from '../utilities/orgRole.ts'
import { roleInOrg } from '../utilities/nodeTenant.ts'

export type { OrgAction }

/**
 * Role gate. The matrix itself lives in `utilities/orgRole.ts` — pure, and therefore testable
 * without React; this hook only supplies the role.
 *
 * With no argument, the role is the one in the ACTIVE org: right for org pages (members, billing,
 * networks). With an `orgId`, it is the role in THAT org. ⚠⚠ Every node control passes the node's
 * tenant, `node.ownerOrgId` (G-1, app#385, docs/PERMISSIONS.md § 2): a node in a network of the
 * active org can belong to another org, and the router asks that org. A null `orgId` (a tenant not
 * known) gives no role, so the control is hidden.
 *
 * ⚠ The router is authoritative. This hides controls so a Member does not click into a guaranteed
 * refusal; it is never the enforcement.
 */
export function useOrgRole(orgId?: string | null) {
  const activeRole = useOrganizations(s => s.role) // 'owner' | 'admin' | 'member' | null
  const organizations = useOrganizations(s => s.organizations)
  const role: OrgRole = orgId === undefined ? (activeRole as OrgRole) : roleInOrg(organizations, orgId)

  const can = (action: OrgAction): boolean => allows(role, action)

  /**
   * Whether the signed-in user controls an object whose owner is `ownerId` (a node's
   * `ownerUserId`, a network's `ownerId`): its owner, or an org Owner/Admin (app#373). The same
   * rule as `mayManageNode` / `mayManageNetwork` / `mayChangeService`, which all share it.
   *
   * ⚠⚠ The Owner/Admin half is asked in the org this hook was given. For a NODE, pass the node's
   * tenant: `useOrgRole(node.ownerOrgId).controls(node.ownerUserId)` is the node's owner or an
   * Owner/Admin of the node's org (Benoit, 2026-09-26, on app#384 / app#386; the router's
   * controllers rule). No role in that org (null) controls nothing, the owner included.
   */
  const controls = (ownerId: string | null | undefined): boolean => mayManageNode(role, ownerId, getUserId())

  return { role, can, controls }
}
